import * as React from "react"
import * as Primitives from "@radix-ui/react-tooltip"
import { clx } from "../utils/clx"

/**
 * Based on [Radix UI Tooltip](https://www.radix-ui.com/primitives/docs/components/tooltip).
 */
interface TooltipProps
  extends Omit<Primitives.TooltipContentProps, "content" | "onClick">,
    Pick<
      Primitives.TooltipProps,
      "open" | "defaultOpen" | "onOpenChange" | "delayDuration"
    > {
  content: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  side?: "bottom" | "left" | "top" | "right"
  showArrow?: boolean
  triggerAsChild?: boolean
}

const Tooltip = React.forwardRef<
  React.ElementRef<typeof Primitives.Content>,
  TooltipProps
>(
  (
    {
      children,
      className,
      content,
      delayDuration,
      defaultOpen,
      open,
      onClick,
      onOpenChange,
      showArrow = true,
      side,
      sideOffset = 10,
      triggerAsChild = false,
      ...props
    }: TooltipProps,
    forwardedRef,
  ) => {
    return (
      <Primitives.Provider delayDuration={150}>
        <Primitives.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          delayDuration={delayDuration}
        >
          <Primitives.Trigger onClick={onClick} asChild={triggerAsChild}>
            {children}
          </Primitives.Trigger>
          <Primitives.Portal>
            <Primitives.Content
              ref={forwardedRef}
              side={side}
              sideOffset={sideOffset}
              align="center"
              className={clx(
                // base
                "max-w-60 select-none rounded-md px-2.5 py-1.5 text-sm leading-5 shadow-md",
                // text color
                "text-grey-0",
                // background color
                "bg-grey-90",
                // transition (tailwindcss-animate; Radix tooltip uses delayed-open | instant-open | closed)
                "will-change-[transform,opacity]",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
                "data-[state=instant-open]:animate-in data-[state=instant-open]:fade-in-0 data-[state=instant-open]:zoom-in-95",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className,
              )}
              {...props}
            >
              {content}
              {showArrow ? (
                <Primitives.Arrow
                  className="border-none fill-grey-90"
                  width={12}
                  height={7}
                  aria-hidden="true"
                />
              ) : null}
            </Primitives.Content>
          </Primitives.Portal>
        </Primitives.Root>
      </Primitives.Provider>
    )
  },
)

Tooltip.displayName = "Tooltip"

export { Tooltip }
